// ink-component
import React, { ReactNode, RefObject } from 'react';

import getGlobalProps from '../../helpers/constants/global-props';
import useInkAnalytics from '../../hooks/useInkAnalytics';

export interface FormProps extends React.HTMLAttributes<HTMLFormElement> {
  /** Unique identifier */
  id: string;
  /** Form contents */
  children?: ReactNode;
  /** onSubmit callback */
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  /** if true and supported by browser, attempt to submit the form when user presses enter key on keyboard */
  allowEnterKeySubmit?: boolean;
  /** HTML action attribute */
  action?: string;
  /** HTML name attribute */
  name?: string;
  /** HTML method attribute */
  method?: string;
  /** Set [autoComplete](/patterns/forms/#autocomplete) */
  autoComplete?: 'on' | 'off';
  /** HTML target attribute */
  target?: string;
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLFormElement>;
  /** Auto scroll user to first error occurrence upon form submission */
  errorAutoScroll?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
}

const globalProps = getGlobalProps({});

/**
 * Setup an observer to watch for new errors being added to the form and scroll to the first occurrence when it appears.
 */
const setupErrorObserver = (form: HTMLFormElement) => {
  const errorSelector = '[role="alert"]';
  const scrollSettings: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' };

  const invalidField = form.querySelector(errorSelector) as HTMLFormElement | null;

  // If there is already an error present, scroll to it
  if (invalidField) {
    invalidField.scrollIntoView(scrollSettings);
  } else {
    // If no errors are present, setup an observer to watch for new errors
    // being added to the form and scroll to the first occurrence when it appears.
    const errorObserverCallback: MutationCallback = (_mutationRecord, mutationObserver) => {
      const invalidField = form.querySelector(errorSelector) as HTMLFormElement | null;
      if (invalidField) {
        invalidField.scrollIntoView(scrollSettings);
      }

      // Disconnect after scroll to prevent scrolling to errors while user is interacting
      // with form elements. Scroll will be retriggered when user submits the form again.
      mutationObserver.disconnect();
    };

    const errorObserver = new MutationObserver(errorObserverCallback);

    errorObserver.observe(form, { childList: true, subtree: true });
  }
};

/**
 * @purpose Helps users provide data, fast and without friction
 * @categories form
 */
function Form(props: FormProps) {
  const {
    name,
    action,
    method,
    target,
    children,
    id,
    onSubmit,
    allowEnterKeySubmit,
    className,
    style,
    externalRef,
    errorAutoScroll,
    ...otherProps
  } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef });

  function handleKeyPress(event: React.KeyboardEvent<HTMLFormElement>) {
    if (allowEnterKeySubmit || event.key !== 'Enter') {
      return;
    }

    const eventTarget = event.target as Element;

    if (eventTarget.tagName !== 'TEXTAREA') {
      /*
       * Textareas still need the default so they break lines.
       * event.target needs to be cast to `Element` because not all event targets are elements.
       */
      event.preventDefault();
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    // Only setup error scrolling when submitting FORM
    if (errorAutoScroll) {
      setupErrorObserver(ref.current);
    }

    if (!action) {
      event.preventDefault();
    }

    if (onSubmit) {
      onSubmit(event);
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <form
      {...globalProps}
      {...otherProps}
      id={id}
      ref={ref}
      name={name}
      action={action}
      method={method}
      target={target}
      onSubmit={handleSubmit}
      onKeyPress={handleKeyPress}
      autoComplete={props.autoComplete}
      data-testid={props['data-testid']}
    >
      {children}
    </form>
  );
}

Form.defaultProps = {
  children: undefined,
  onSubmit: undefined,
  action: undefined,
  name: undefined,
  method: undefined,
  autoComplete: 'off',
  target: undefined,
  allowEnterKeySubmit: false,
  errorAutoScroll: false,
  'data-testid': undefined,
  'data-track-events': ['submit'],
};

Form.displayName = 'Form';

export default Form;
